import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import i18next from "src/i18n/config";
import { makeStyles } from "tss-react/mui";

import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { Box, Theme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { PrimaryOutlinedButton } from "@shared/CustomButton";
import { SectionField, SectionFields } from "@shared/SectionPanel/SectionField";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { Tooltip } from "@shared/Tooltip";
import { Elements } from "@stripe/react-stripe-js";

import { useAccountBillingCard } from "../../hooks/useAccountBillingCard";
import { useAccountProfile } from "../../hooks/useAccountProfile";

import { BillingEditForm } from "./BillingEditForm";
import EditStripeCard from "./EditStripeCard";

const useStyles = makeStyles()((theme: Theme) => ({
    content: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
        width: "100%",
    },
}));

function displayValue(value: string | null | undefined) {
    return value?.trim() ? value : "-";
}

function capitalize(value: string | null | undefined) {
    if (!value?.trim()) {
        return "-";
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function AccountBillingPage() {
    const [isModalShown, setIsModalShown] = React.useState(false);

    const { t } = useTranslation(["common", "pages"]);
    const { classes } = useStyles();
    const { billingAddress, isLoading: isProfileLoading, refetch } = useAccountProfile();
    const { billingCard, stripePromise, isStripeEnabled, isLoading: isBillingCardLoading } = useAccountBillingCard();

    const [searchParams, setSearchParams] = useSearchParams();
    const mode = searchParams.get("mode");

    const clearMode = React.useCallback(() => setSearchParams({}, { replace: true }), [setSearchParams]);
    const handleBillingEditSuccess = React.useCallback(() => {
        clearMode();
        void refetch();
    }, [clearMode, refetch]);

    return (
        <PageContainer fullWidth={false} isLoading={isProfileLoading || isBillingCardLoading}>
            <Box className={classes.content}>
                {mode === "edit" ? (
                    <BillingEditForm billingAddress={billingAddress} onSuccess={handleBillingEditSuccess} onCancel={clearMode} />
                ) : (
                    <SectionPanel
                        title={t("pages:settingsPage.billingAddress.title")}
                        actions={
                            <Link to="?mode=edit">
                                <PrimaryOutlinedButton
                                    size="small"
                                    startIcon={<i className="fa-solid fa-fw fa-pencil" />}
                                    name="edit-billing-address"
                                >
                                    {t("common:button.edit")}
                                </PrimaryOutlinedButton>
                            </Link>
                        }
                    >
                        <SectionFields>
                            <SectionField
                                label={t("pages:settingsPage.profileData.name")}
                                value={displayValue(billingAddress?.name)}
                            />
                            <SectionField label={t("common:label.address")} value={displayValue(billingAddress?.address)} />
                            <SectionField
                                label={t("pages:settingsPage.profileData.city")}
                                value={capitalize(billingAddress?.city)}
                            />
                            <SectionField
                                label={t("pages:settingsPage.profileData.zipCode")}
                                value={displayValue(billingAddress?.zipCode)}
                            />
                            <SectionField
                                label={t("pages:settingsPage.profileData.country")}
                                value={displayValue(billingAddress?.country)}
                            />
                        </SectionFields>
                    </SectionPanel>
                )}
                {isStripeEnabled ? (
                    <SectionPanel
                        title={t("pages:settingsPage.paymentMethod.title")}
                        actions={
                            <Tooltip placement="top" title={t("pages:settingsPage.paymentMethod.editTooltip")}>
                                <PrimaryOutlinedButton
                                    startIcon={<i className="fa-solid fa-fw fa-pencil" />}
                                    onClick={() => setIsModalShown(true)}
                                    size="small"
                                    disabled
                                    name="edit-payment-method"
                                >
                                    {t("common:button.edit")}
                                </PrimaryOutlinedButton>
                            </Tooltip>
                        }
                    >
                        <SectionFields>
                            <SectionField
                                label={t("pages:settingsPage.paymentMethod.cardType")}
                                value={
                                    billingCard?.brand
                                        ? capitalize(billingCard.brand)
                                        : t("pages:settingsPage.paymentMethod.noCardSet")
                                }
                            />
                            <SectionField
                                label={t("pages:settingsPage.paymentMethod.cardNumber")}
                                value={
                                    billingCard?.lastFourDigits
                                        ? `**** **** **** ${billingCard.lastFourDigits}`
                                        : t("pages:settingsPage.paymentMethod.noCardNumber")
                                }
                            />
                            <SectionField
                                label={t("common:label.expirationDate")}
                                value={`${billingCard?.expMonth ?? "MM"} / ${billingCard?.expYear ?? "YY"}`}
                            />
                        </SectionFields>
                    </SectionPanel>
                ) : null}
                {stripePromise && isStripeEnabled ? (
                    <Elements stripe={stripePromise}>
                        <EditStripeCard open={isModalShown} onClose={() => setIsModalShown(false)} />
                    </Elements>
                ) : null}
            </Box>
        </PageContainer>
    );
}

export default withMetaDescription(AccountBillingPage, {
    title: i18next.t("pages:settingsPage.pages.billing.metaTitle"),
    description: i18next.t("pages:settingsPage.pages.billing.metaDescription"),
});
